import React from 'react';

import { CodeSnippet } from '@backstage/core-components';
import { ReviewStepProps } from '@backstage/plugin-scaffolder-react';

import { Box } from '@material-ui/core';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles(theme => ({
  content: {
    marginBottom: theme.spacing(2),
    marginLeft: theme.spacing(3),
    marginRight: theme.spacing(3),
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing(1),
    marginTop: theme.spacing(3),
  },
}));

function isInternalKey(key: string): boolean {
  return key.startsWith('_') || key.endsWith('Ref') || key.endsWith('ref');
}

function isPassword(key: string, steps: ReviewStepProps['steps']): boolean {
  for (const step of steps) {
    const widget = step.uiSchema?.[key]?.['ui:widget'];
    const type = (step.mergedSchema as any)?.properties?.[key]?.type;
    if (widget === 'password' || type === 'password') return true;
  }
  return false;
}

function getFieldTitle(key: string, schema: Record<string, any>): string {
  return schema?.properties?.[key]?.title ?? key;
}

function buildStepData(
  formData: Record<string, any>,
  step: ReviewStepProps['steps'][number],
  steps: ReviewStepProps['steps'],
): Record<string, any> {
  const schema = step.mergedSchema as Record<string, any>;
  const properties = Object.keys(schema?.properties ?? {});
  const result: Record<string, any> = {};

  for (const key of properties) {
    if (isInternalKey(key)) continue;
    const value = formData[key];
    if (value === undefined || value === '') continue;

    const label = getFieldTitle(key, schema);

    if (isPassword(key, steps)) {
      result[label] = '••••••••';
    } else {
      result[label] = value;
    }
  }

  return result;
}

export const CustomReviewStep = ({
  formData,
  steps,
  handleBack,
  handleCreate,
  disableButtons,
}: ReviewStepProps) => {
  const styles = useStyles();

  return (
    <Box>
      <Box className={styles.content}>
        <Typography variant="h5" gutterBottom>
          Provided inputs
        </Typography>
        <Box>
          <CodeSnippet
            text={JSON.stringify(
              steps.reduce(
                (acc, step) =>
                  Object.assign(acc, buildStepData(formData, step, steps)),
                {},
              ),
              null,
              2,
            )}
            language="json"
            showLineNumbers
            showCopyCodeButton
          />
        </Box>
      </Box>

      <Box className={styles.actions}>
        <Button onClick={handleBack} disabled={disableButtons}>
          Back
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleCreate}
          disabled={disableButtons}
        >
          {disableButtons ? (
            <>
              <CircularProgress size={14} style={{ marginRight: 6 }} />
              Creating…
            </>
          ) : (
            'Create'
          )}
        </Button>
      </Box>
    </Box>
  );
};
